import { AttributeType, EqlAttributesConfigurationDc, StringSubType } from "@evergis/api";
import { useCallback, useState } from "react";
import { EqlAttribute } from "../types";

// eslint-disable-next-line max-lines-per-function
export function useQueryAttributes(
  initialAttributesConfiguration: EqlAttributesConfigurationDc,
  nameField = "attributeName",
) {
  const [attributesConfiguration, setAttributesConfiguration] =
    useState<EqlAttributesConfigurationDc>(initialAttributesConfiguration);

  const { attributes } = attributesConfiguration;

  const setAttributes = useCallback(
    newAttributes => {
      setAttributesConfiguration(current => ({ ...current, attributes: newAttributes }));
    },
    [setAttributesConfiguration],
  );

  const updateAttribute = useCallback(
    (attributeName: string, attributeUpdate: Partial<EqlAttribute>) =>
      setAttributesConfiguration(current => ({
        ...current,
        attributes: current?.attributes?.map(attribute => {
          if (attribute[nameField] !== attributeName) return attribute;

          let modifiedAttribute;

          if (attributeUpdate.subType === StringSubType.Image) {
            modifiedAttribute = { ...attributeUpdate, type: AttributeType.String, subType: StringSubType.Image };
          } else if (attributeUpdate.subType === StringSubType.Attachments) {
            modifiedAttribute = { ...attributeUpdate, type: AttributeType.Json, subType: StringSubType.Attachments };
          } else if (!attributeUpdate.type) {
            modifiedAttribute = { ...attributeUpdate };
          } else {
            modifiedAttribute = { ...attributeUpdate, subType: undefined };
          }

          return { ...attribute, ...modifiedAttribute };
        }),
      })),
    [setAttributesConfiguration],
  );

  const deleteAttribute = useCallback(
    attributeName =>
      setAttributesConfiguration(current => ({
        ...current,
        attributes: current?.attributes?.filter(attribute => attribute[nameField] !== attributeName),
      })),
    [setAttributesConfiguration],
  );

  const addAttribute = useCallback(
    (attribute: EqlAttribute) =>
      setAttributesConfiguration(current => ({
        ...current,
        attributes: current.attributes ? [...current.attributes, attribute] : [attribute],
      })),
    [setAttributesConfiguration],
  );

  const setDisplayField = useCallback(
    titleAttribute => setAttributesConfiguration(current => ({ ...current, titleAttribute })),
    [setAttributesConfiguration],
  );

  return {
    attributesConfiguration,
    setAttributesConfiguration,
    attributes,
    setAttributes,
    updateAttribute,
    deleteAttribute,
    addAttribute,
    setDisplayField,
  };
}
